import { useEffect, useMemo, useRef, useState } from "react";
import { API_BASE_URL, TIP_LINK } from "./config";
import EventList from "./EventList";
import "./global.css";
import "./components.css";

const CITY = "New York";
const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const toIsoDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseIsoDate = (iso) => {
  if (!iso) return null;
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const formatDateLabel = (iso) => {
  const date = parseIsoDate(iso);
  if (!date) return "";
  return date.toLocaleDateString([], { month: "numeric", day: "numeric" });
};

const monthKey = (date) => date.getFullYear() * 12 + date.getMonth();

function App() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectionStage, setSelectionStage] = useState("start");
  const pickerRef = useRef(null);
  const todayDate = useMemo(() => new Date(), []);
  const todayIso = useMemo(() => toIsoDate(todayDate), [todayDate]);
  const minMonth = useMemo(
    () => new Date(todayDate.getFullYear(), todayDate.getMonth(), 1),
    [todayDate]
  );
  const [calendarMonth, setCalendarMonth] = useState(minMonth);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const dateParam = params.get("date");
    const startDateParam = params.get("start_date");
    const endDateParam = params.get("end_date");
    let initialStart = "";
    let initialEnd = "";

    if (startDateParam || endDateParam) {
      initialStart = startDateParam || endDateParam || "";
      initialEnd = endDateParam || startDateParam || "";
    } else if (dateParam) {
      initialStart = dateParam;
      initialEnd = dateParam;
    }

    if (initialStart && initialEnd && initialEnd < initialStart) {
      [initialStart, initialEnd] = [initialEnd, initialStart];
    }

    if (initialStart) {
      setStartDate(initialStart);
      setEndDate(initialEnd || initialStart);
      const initialMonth = parseIsoDate(initialStart);
      if (initialMonth) {
        setCalendarMonth(new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1));
      }
    }
  }, []);

  useEffect(() => {
    if (!isPickerOpen) return undefined;

    const handlePointerDown = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsPickerOpen(false);
        setSelectionStage("start");
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isPickerOpen]);

  const normalizedRange = useMemo(() => {
    if (!startDate || !endDate) return { start: "", end: "" };
    return startDate <= endDate
      ? { start: startDate, end: endDate }
      : { start: endDate, end: startDate };
  }, [startDate, endDate]);

  const rangeLabel = useMemo(() => {
    if (!normalizedRange.start) return "Select a date or range";
    if (normalizedRange.start === normalizedRange.end) {
      return formatDateLabel(normalizedRange.start);
    }
    return `${formatDateLabel(normalizedRange.start)} - ${formatDateLabel(normalizedRange.end)}`;
  }, [normalizedRange]);

  const calendarDays = useMemo(() => {
    const firstOfMonth = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const startOffset = firstOfMonth.getDay();
    const gridStart = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1 - startOffset);

    return Array.from({ length: 42 }, (_, index) => {
      const day = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + index);
      const iso = toIsoDate(day);
      const inCurrentMonth = day.getMonth() === calendarMonth.getMonth();
      const isDisabled = iso < todayIso;
      const isSelectedStart = normalizedRange.start && iso === normalizedRange.start;
      const isSelectedEnd = normalizedRange.end && iso === normalizedRange.end;
      const isInRange =
        normalizedRange.start &&
        normalizedRange.end &&
        iso > normalizedRange.start &&
        iso < normalizedRange.end;

      return {
        iso,
        label: day.getDate(),
        inCurrentMonth,
        isDisabled,
        isSelected: isSelectedStart || isSelectedEnd,
        isInRange,
      };
    });
  }, [calendarMonth, normalizedRange, todayIso]);

  const monthLabel = useMemo(
    () => calendarMonth.toLocaleDateString([], { month: "long", year: "numeric" }),
    [calendarMonth]
  );
  const canGoPrevMonth = monthKey(calendarMonth) > monthKey(minMonth);

  const stats = useMemo(() => ({ shows: events.length }), [events]);
  const countLabel = stats.shows === 1 ? "concert found" : "concerts found";

  const handleDatePick = (iso) => {
    if (iso < todayIso) return;

    if (selectionStage === "start") {
      setStartDate(iso);
      setEndDate(iso);
      setSelectionStage("end");
      return;
    }

    const baseStart = startDate || iso;
    if (iso < baseStart) {
      setStartDate(iso);
      setEndDate(baseStart);
    } else {
      setStartDate(baseStart);
      setEndDate(iso);
    }

    setSelectionStage("start");
  };

  const togglePicker = () => {
    if (isPickerOpen) {
      setIsPickerOpen(false);
      setSelectionStage("start");
      return;
    }

    const focusDate = parseIsoDate(normalizedRange.start || todayIso);
    if (focusDate) {
      setCalendarMonth(new Date(focusDate.getFullYear(), focusDate.getMonth(), 1));
    }
    setIsPickerOpen(true);
  };

  const fetchEvents = async () => {
    if (!normalizedRange.start || !normalizedRange.end) {
      setError("Please select a date or date range.");
      return;
    }

    setLoading(true);
    setError("");
    setHasSearched(true);

    try {
      const params = new URLSearchParams();
      params.append("city", CITY);
      params.append("start_date", normalizedRange.start);
      params.append("end_date", normalizedRange.end);

      const res = await fetch(`${API_BASE_URL}/events?${params.toString()}`);
      const data = await res.json();

      setEvents(data.events || []);
    } catch {
      setError("Network error. Please try again.");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <header className="header">
        <h1>NYC Jazz Concerts</h1>
      </header>

      <div className="controls">
        <div className="field range-field" ref={pickerRef}>
          <span>Date range</span>
          <button
            type="button"
            className={`date-range-trigger${isPickerOpen ? " is-open" : ""}`}
            onClick={togglePicker}
          >
            {rangeLabel}
          </button>
          {isPickerOpen && (
            <div className="date-range-popover">
              <div className="calendar-header">
                <button
                  type="button"
                  className="calendar-nav"
                  onClick={() =>
                    setCalendarMonth(
                      new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1)
                    )
                  }
                  disabled={!canGoPrevMonth}
                >
                  Prev
                </button>
                <strong>{monthLabel}</strong>
                <button
                  type="button"
                  className="calendar-nav"
                  onClick={() =>
                    setCalendarMonth(
                      new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1)
                    )
                  }
                >
                  Next
                </button>
              </div>

              <div className="calendar-weekdays">
                {WEEKDAY_LABELS.map((dayLabel) => (
                  <span key={dayLabel}>{dayLabel}</span>
                ))}
              </div>

              <div className="calendar-grid">
                {calendarDays.map((day) => (
                  <button
                    key={day.iso}
                    type="button"
                    className={[
                      "calendar-day",
                      day.isSelected ? "is-selected" : "",
                      day.isInRange ? "is-in-range" : "",
                      day.inCurrentMonth ? "" : "is-outside",
                      day.isDisabled ? "is-disabled" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => handleDatePick(day.iso)}
                    disabled={day.isDisabled}
                  >
                    {day.label}
                  </button>
                ))}
              </div>

              <div className="calendar-footer">
                <button
                  type="button"
                  className="calendar-action-btn"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    setSelectionStage("start");
                  }}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="calendar-action-btn is-primary"
                  onClick={() => {
                    setIsPickerOpen(false);
                    setSelectionStage("start");
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="control-actions">
          <button onClick={fetchEvents}>Find concerts</button>
          <a
            className="tip-button"
            href={TIP_LINK}
            target="_blank"
            rel="noopener noreferrer"
          >
            Tip via Stripe
          </a>
        </div>
      </div>

      {loading && <div className="notice is-muted">Loading shows...</div>}
      {error && <div className="notice">{error}</div>}
      {!loading && !error && hasSearched && events.length === 0 && (
        <div className="notice is-muted">
          No shows found. Try different dates.
        </div>
      )}

      {hasSearched && !loading && !error && (
        <div className="results-count">
          {stats.shows} {countLabel}
        </div>
      )}

      <EventList events={events} />
    </div>
  );
}

export default App;
